import React from 'react';
import PropTypes from 'prop-types';
import { Table, Button, Badge } from 'react-bootstrap';

/**
 * RuleSetTable - Presentational component for displaying rule set data
 * 
 * @param {Array} rulesets - Array of ruleset objects
 * @param {boolean} loading - Loading state indicator
 * @param {Function} onRulesetClick - Callback when a ruleset row is clicked
 * @param {Function} onEdit - Callback when edit button is clicked
 * @param {Function} onDelete - Callback when delete button is clicked
 */
const RuleSetTable = ({ rulesets, loading, onRulesetClick, onEdit, onDelete }) => {
  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!rulesets || rulesets.length === 0) {
    return (
      <div className="text-center p-4 text-muted">
        No rule sets found. Click "Add Rule Set" to create one.
      </div>
    );
  }

  return (
    <Table striped bordered hover responsive>
      <thead>
        <tr>
          <th>Name</th>
          <th>Short Code</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {rulesets.map(ruleset => (
          <tr key={ruleset.id}>
            <td
              onClick={() => onRulesetClick && onRulesetClick(ruleset.id)}
              style={{ cursor: onRulesetClick ? 'pointer' : 'default' }}
            >
              {ruleset.name}
            </td>
            <td>{ruleset.shortCode}</td>
            <td>
              <Badge bg={ruleset.isActive ? 'success' : 'secondary'}>
                {ruleset.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </td>
            <td>
              <Button
                variant="outline-secondary"
                size="sm"
                className="me-2"
                onClick={() => onRulesetClick && onRulesetClick(ruleset.id)}
              >
                Details
              </Button>
              <Button 
                variant="outline-primary" 
                size="sm" 
                className="me-2"
                onClick={() => onEdit(ruleset)}
              >
                Edit
              </Button>
              <Button 
                variant="outline-danger" 
                size="sm"
                onClick={() => onDelete(ruleset.id)}
              >
                Delete
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

RuleSetTable.propTypes = {
  rulesets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      shortCode: PropTypes.string,
      isActive: PropTypes.bool
    })
  ).isRequired,
  loading: PropTypes.bool,
  onRulesetClick: PropTypes.func,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
};

RuleSetTable.defaultProps = {
  loading: false,
  onRulesetClick: null
};

export default RuleSetTable;
